import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { adminAPI, authAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';
import '../styles/admins.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const initialForm = {
  name: '',
  email: '',
  role: 'admin',
  password: '',
  confirmPassword: '',
};

const Admins = () => {
  const currentAdmin = JSON.parse(localStorage.getItem('admin') || 'null');
  const isSuperAdmin = currentAdmin?.role === 'super_admin';
  const [admins, setAdmins] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [adminStatusFilter, setAdminStatusFilter] = useState('active');
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!isSearchReady) {
      return;
    }
    fetchAdmins();
  }, [debouncedSearch, adminStatusFilter, currentPage, pageSize]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAll({
        search: normalizedSearch,
        adminStatus: adminStatusFilter,
        page: currentPage,
        limit: pageSize,
      });

      setAdmins(response.data.data.admins || []);
      setTotalPages(response.data.data.pages || 1);
      setTotalAdmins(response.data.data.total || 0);
    } catch (fetchError) {
      setError(fetchError.response?.data?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setSelectedAdmin(null);
    setFormData(initialForm);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setIsEditMode(true);
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      password: '',
      confirmPassword: '',
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = (force = false) => {
    if (saving && !force) return;
    setShowModal(false);
    setFormData(initialForm);
    setSelectedAdmin(null);
    setError('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const refreshCurrentAdminIfNeeded = async (updatedAdmin) => {
    if (!currentAdmin || !updatedAdmin || Number(currentAdmin.id) !== Number(updatedAdmin.id)) {
      return;
    }

    try {
      const response = await authAPI.getMe();
      localStorage.setItem('admin', JSON.stringify(response.data.data.admin));
    } catch (refreshError) {
      console.error('Failed to refresh current admin:', refreshError);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required');
      return;
    }

    if (!isEditMode && !formData.password) {
      setError('Password is required when creating an admin');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Password and confirm password must match');
      return;
    }

    setSaving(true);

    try {
      if (isEditMode) {
        const payload = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
        };

        if (formData.password) {
          payload.password = formData.password;
        }

        const response = await adminAPI.update(selectedAdmin.id, payload);
        await refreshCurrentAdminIfNeeded(response.data.data.admin);
        setSuccess('Admin updated successfully');
      } else {
        await adminAPI.create({
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          password: formData.password,
        });
        setSuccess('Admin created successfully');
      }

      closeModal(true);
      fetchAdmins();
    } catch (submitError) {
      setError(submitError.response?.data?.message || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (admin) => {
    const confirmed = window.confirm(`Delete admin "${admin.name}"?`);
    if (!confirmed) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await adminAPI.delete(admin.id);
      setSuccess('Admin moved to ex admins successfully');

      if (admins.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchAdmins();
      }
    } catch (deleteError) {
      setError(deleteError.response?.data?.message || 'Failed to delete admin');
    }
  };

  const handleRestore = async (admin) => {
    const confirmed = window.confirm(`Move admin "${admin.name}" back to active?`);
    if (!confirmed) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await adminAPI.restore(admin.id);
      setSuccess('Admin moved back to active successfully');

      if (admins.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchAdmins();
      }
    } catch (restoreError) {
      setError(restoreError.response?.data?.message || 'Failed to restore admin');
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <div>
            <h1>Admin Management</h1>
            <p className="admin-page-subtitle">Super admins can create, update, delete, and restore admin accounts.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <i className="fas fa-plus"></i> Add Admin
          </button>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && !showModal && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Type at least 3 characters to search by name, email, or role..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <i className="fas fa-magnifying-glass"></i>
            <select
              value={adminStatusFilter}
              onChange={(e) => {
                setAdminStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">Current Admins</option>
              <option value="deleted">Ex Admins</option>
            </select>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : admins.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((admin) => {
                      const isCurrentAdmin = Number(currentAdmin?.id) === Number(admin.id);
                      const isExAdmin = Number(admin.isDeleted) === 1;

                      return (
                        <tr key={admin.id}>
                          <td>
                            <div className="admin-name-cell">
                              <span>{admin.name}</span>
                              {isCurrentAdmin && <span className="badge badge-success">You</span>}
                            </div>
                          </td>
                          <td>{admin.email}</td>
                          <td>
                            <span className={`badge ${admin.role === 'super_admin' ? 'badge-warning' : 'badge-success'}`}>
                              {admin.role}
                            </span>
                          </td>
                          <td>{new Date(admin.created_at).toLocaleDateString('en-IN')}</td>
                          <td className='width-180'>
                            <div className="action-buttons">
                              <button
                                className="btn btn-secondary"
                                onClick={() => openEditModal(admin)}
                                disabled={isExAdmin}
                                title={isExAdmin ? 'Ex admins cannot be edited' : 'Edit admin'}
                              >
                                <i className="fas fa-pen"></i> Edit
                              </button>
                              {isExAdmin ? (
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleRestore(admin)}
                                  disabled={!isSuperAdmin}
                                  title={isSuperAdmin ? 'Move admin back to active' : 'Only super admin can restore admins'}
                                >
                                  <i className="fas fa-rotate-left"></i> Restore
                                </button>
                              ) : (
                                <button
                                  className="btn btn-danger"
                                  onClick={() => handleDelete(admin)}
                                  disabled={isCurrentAdmin}
                                  title={isCurrentAdmin ? 'You cannot delete your own account' : 'Move admin to ex admins'}
                                >
                                  <i className="fas fa-trash"></i> Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fee-pagination-section">
                <div className="pagination-left">
                  <label className="rows-per-page">
                    Rows per page:
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setCurrentPage(1);
                      }}
                      className="page-size-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                  <span className="page-info">Total: {totalAdmins} admins</span>
                </div>

                <div className="pagination-center">
                  <span className="page-indicator">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                </div>

                <div className="pagination-controls">
                  <button className="btn btn-pagination" onClick={handlePreviousPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <button className="btn btn-pagination" onClick={handleNextPage} disabled={currentPage === totalPages}>
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              {adminStatusFilter === 'deleted'
                ? 'No ex admins found.'
                : 'No admins found. Create the first additional admin to get started.'}
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content admin-modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h2>{isEditMode ? 'Edit Admin' : 'Create Admin'}</h2>
                <button className="modal-close" onClick={closeModal} type="button">
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="alert alert-error">{error}</div>}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder='Enter full name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder='Enter email address'
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Role</label>
                      <select name="role" value={formData.role} onChange={handleChange}>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{isEditMode ? 'New Password' : 'Password'}</label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={isEditMode ? 'Leave blank to keep current password' : 'Enter password'}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{isEditMode ? 'Confirm New Password' : 'Confirm Password'}</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : isEditMode ? 'Update Admin' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Admins;
